// Card-view tile for a hosted site — used in the grid layout of HostedSitesPage

import { useState } from 'react'
import { FileCode, ChevronDown } from 'lucide-react'
import { cn } from '../../lib/utils'
import { formatBytes, formatDate, sitePublicUrl } from './utils'
import { SiteActions } from './site-actions'
import { SiteDisclosure } from './site-disclosure'
import type { HostedSiteRowProps } from './hosted-site-row'

// Card reuses the same props shape as HostedSiteRow so the orchestrator can swap them.
type HostedSiteCardProps = HostedSiteRowProps

export function HostedSiteCard({
  site,
  isDark,
  expandedId,
  toggleExpanded,
  editingId,
  draft,
  setDraft,
  summaryFull,
  setSummaryFull,
  pendingSummaryId,
  summaryError,
  copiedId,
  previewSite,
  onCopyLink,
  onTogglePreview,
  onDelete,
  onStartEdit,
  onCancelEdit,
  onCommitEdit,
  onRegenerate,
}: HostedSiteCardProps) {
  const publicUrl = sitePublicUrl(site)
  const isExpanded = expandedId === site.id

  // Hover lift handled via CSS — track hover for border highlight
  const [hovered, setHovered] = useState(false)

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={cn(
        'group flex flex-col rounded-xl border p-4 transition-all duration-150',
        isDark
          ? cn(
              'border-white/[0.06] bg-surface-1',
              hovered && 'border-white/[0.12] bg-surface-2 -translate-y-0.5 shadow-lg shadow-black/20',
            )
          : cn(
              'border-neutral-200 bg-white',
              hovered && 'border-neutral-300 -translate-y-0.5 shadow-md shadow-neutral-200/60',
            ),
      )}
    >
      {/* Header: icon + status badge */}
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
          isDark ? 'bg-surface-3' : 'bg-neutral-100',
        )}>
          <FileCode className={cn(
            'h-5 w-5',
            site.status === 'ready'
              ? (isDark ? 'text-brand-400' : 'text-brand-600')
              : 'text-neutral-400',
          )} />
        </div>
        <span className={cn(
          'shrink-0 rounded-md px-2 py-0.5 text-[10px] font-medium',
          site.status === 'ready'
            ? 'bg-green-500/10 text-green-500'
            : site.status === 'failed'
              ? 'bg-red-500/10 text-red-500'
              : 'bg-yellow-500/10 text-yellow-500',
        )}>
          {site.status}
        </span>
      </div>

      {/* Filename */}
      <p className={cn(
        'mb-1 truncate text-sm font-semibold leading-snug',
        isDark ? 'text-neutral-200' : 'text-neutral-800',
      )}>
        {site.filename}
      </p>

      {/* Meta: size + date */}
      <p className={cn('mb-3 text-xs', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
        {formatBytes(site.totalSizeBytes)} · {site.fileCount} {site.fileCount === 1 ? 'file' : 'files'} · {formatDate(site.createdAt)}
      </p>

      {/* Description preview (line-clamp) */}
      {site.description && (
        <p className={cn(
          'mb-2 line-clamp-2 text-xs leading-relaxed',
          isDark ? 'text-neutral-400' : 'text-neutral-600',
        )}>
          {site.description}
        </p>
      )}

      {/* Summary preview (line-clamp-2) */}
      {site.summary && (
        <p className={cn(
          'mb-3 line-clamp-2 text-xs italic leading-relaxed',
          isDark ? 'text-neutral-500' : 'text-neutral-500',
        )}>
          {site.summary}
        </p>
      )}

      {/* Spacer pushes actions to bottom */}
      <div className="flex-1" />

      {/* Details toggle + mobile-style actions row at bottom */}
      <div className={cn(
        'mt-3 border-t pt-3',
        isDark ? 'border-white/[0.06]' : 'border-neutral-100',
      )}>
        <SiteActions
          site={site}
          isDark={isDark}
          copiedId={copiedId}
          previewSite={previewSite}
          publicUrl={publicUrl}
          onCopyLink={onCopyLink}
          onTogglePreview={onTogglePreview}
          onDelete={onDelete}
          variant="mobile"
        />

        {/* Details disclosure toggle */}
        <button
          type="button"
          onClick={() => toggleExpanded(site.id)}
          className={cn(
            'mt-2 flex w-full items-center justify-center gap-1 rounded-md py-1 text-[11px] transition-colors',
            isDark
              ? 'text-neutral-500 hover:bg-surface-3 hover:text-neutral-300'
              : 'text-neutral-400 hover:bg-neutral-50 hover:text-neutral-600',
          )}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Hide details' : 'Show details'}
        >
          <span>{isExpanded ? 'Hide details' : 'Details'}</span>
          <ChevronDown className={cn('h-3 w-3 transition-transform', isExpanded && 'rotate-180')} />
        </button>
      </div>

      {/* Disclosure panel — description editor + AI summary */}
      {isExpanded && (
        <SiteDisclosure
          site={site}
          isDark={isDark}
          isEditing={editingId === site.id}
          draft={draft}
          setDraft={setDraft}
          summaryFull={summaryFull}
          setSummaryFull={setSummaryFull}
          pendingSummaryId={pendingSummaryId}
          summaryError={summaryError}
          onStartEdit={onStartEdit}
          onCancelEdit={onCancelEdit}
          onCommitEdit={onCommitEdit}
          onRegenerate={onRegenerate}
        />
      )}
    </div>
  )
}
